import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, Component, Input, HostBinding, HostListener, Directive, NgModule } from '@angular/core';
import * as i1 from 'se-fe-position';
import { SeFePositionModule } from 'se-fe-position';
import { BehaviorSubject } from 'rxjs';

class SeFeTooltipService {
    constructor() {
        this.tooltips = new Map();
        this.triggers = new Map();
        this.contents = new Map();
    }
    initTooltip(id) {
        if (!this.tooltips.has(id)) {
            this.tooltips.set(id, new BehaviorSubject(false));
            this.triggers.set(id, false);
            this.contents.set(id, false);
        }
    }
    tooltipVisible(id) {
        var _a;
        return (_a = this.tooltips.get(id)) === null || _a === void 0 ? void 0 : _a.asObservable();
    }
    enterTrigger(id) {
        this.triggers.set(id, true);
        this.showTooltip(id);
    }
    leaveTrigger(id) {
        this.triggers.set(id, false);
        this.checkHideTooltip(id);
    }
    enterContent(id) {
        this.contents.set(id, true);
    }
    leaveContent(id) {
        this.contents.set(id, false);
        this.checkHideTooltip(id);
    }
    showTooltip(id) {
        var _a;
        (_a = this.tooltips.get(id)) === null || _a === void 0 ? void 0 : _a.next(true);
    }
    hideTooltip(id) {
        var _a;
        (_a = this.tooltips.get(id)) === null || _a === void 0 ? void 0 : _a.next(false);
    }
    checkHideTooltip(id) {
        setTimeout(() => {
            if (!this.triggers.get(id) && !this.contents.get(id)) {
                this.hideTooltip(id);
            }
        }, 100);
    }
}
SeFeTooltipService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SeFeTooltipService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SeFeTooltipComponent {
    constructor(cdr, elementRef, seFePositionService, seFeTooltipService) {
        this.cdr = cdr;
        this.elementRef = elementRef;
        this.seFePositionService = seFePositionService;
        this.seFeTooltipService = seFeTooltipService;
        this.visible = false;
        this.position = 'top';
        this.timer = null;
        this.enableTopLevelScrollParent = true;
        this.role = 'tooltip';
        this.show = () => this.visible = true;
        this.onScroll = () => {
            this.hide();
            if (this.timer !== null) {
                clearTimeout(this.timer);
            }
            this.timer = setTimeout(() => {
                this.seFePositionService.contents.forEach(element => {
                    if (element.visible)
                        element.hide();
                });
            }, 100);
        };
        this.id = `se-fe-tooltip-${Date.now()}-${SeFeTooltipComponent.counter++}`;
    }
    get visibleClass() {
        return this.visible;
    }
    onMouseEnter() {
        this.seFeTooltipService.enterContent(this.name);
    }
    onMouseLeave() {
        this.seFeTooltipService.leaveContent(this.name);
    }
    hide() {
        this.visible = false;
        this.cdr.markForCheck();
    }
    get element() {
        return this.elementRef.nativeElement;
    }
    ngOnInit() {
        this.seFePositionService.addContent(this);
        this.seFeTooltipService.initTooltip(this.name);
        this.subscription = this.seFeTooltipService.tooltipVisible(this.name).subscribe(visible => {
            visible ? this.trigger.open() : this.trigger.close();
        });
    }
    ngAfterViewInit() {
        const root = this.element && this.element.getRootNode();
        const shadowDomExists = this.isShadowRoot(root);
        this.eventListenerEl = shadowDomExists ? root : document;
        this.eventListenerEl.addEventListener('scroll', this.onScroll, false);
    }
    ngOnDestroy() {
        this.seFePositionService.removeContent(this);
        this.eventListenerEl.removeEventListener('scroll', this.onScroll);
        this.subscription.unsubscribe();
    }
    isShadowRoot(el) {
        return el instanceof ShadowRoot;
    }
    get trigger() {
        return this.seFePositionService.triggers.find(t => t.instance.name === this.name);
    }
}
SeFeTooltipComponent.counter = 1;
SeFeTooltipComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i1.SeFePositionService }, { token: SeFeTooltipService }], target: i0.ɵɵFactoryTarget.Component });
SeFeTooltipComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeTooltipComponent, selector: "se-fe-tooltip", inputs: { name: ["seFeDataName", "name"] }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()" }, properties: { "attr.id": "this.id", "class.se-fe-tooltip--visible": "this.visibleClass", "attr.role": "this.role" } }, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true, styles: [":host{font:normal 400 calc(var(--se-rem, 1rem) * .8571428571)/1.4286 \"Open Sans\",sans-serif;z-index:-999;opacity:0;position:absolute;display:block;transition:opacity;cursor:pointer;background:rgba(35,40,43,.9);background-clip:padding-box;border:8px solid transparent;border-radius:12px;color:#fff;max-width:300px;padding:8px;text-align:center}:host.se-fe-tooltip--visible{opacity:1;z-index:999;pointer-events:all;transition:opacity .2s .4s}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-tooltip',
                    template: `<ng-content></ng-content>`,
                    styleUrls: ['./se-fe-tooltip.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i1.SeFePositionService }, { type: SeFeTooltipService }]; }, propDecorators: { name: [{
                type: Input,
                args: ['seFeDataName']
            }], id: [{
                type: HostBinding,
                args: ['attr.id']
            }], visibleClass: [{
                type: HostBinding,
                args: ['class.se-fe-tooltip--visible']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

var Keys;
(function (Keys) {
    Keys["ENTER"] = "Enter";
    Keys["SPACE"] = "Space";
    Keys["TAB"] = "Tab";
    Keys["ESC"] = "Escape";
})(Keys || (Keys = {}));
class SeFeTooltipTriggerDirective {
    constructor(elementRef, seFePositionService, seFeTooltipService, cdr) {
        this.elementRef = elementRef;
        this.seFePositionService = seFePositionService;
        this.seFeTooltipService = seFeTooltipService;
        this.cdr = cdr;
        this.event = 'click';
        this.cursor = 'pointer';
        //noop
    }
    get nameProxy() { return this.name || ''; }
    get desc() {
        var _a;
        return ((_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.id) || null;
    }
    onKedown(event) {
        switch (event.code) {
            case Keys.ENTER:
            case Keys.SPACE:
                this.trigger.toggle();
                break;
            case Keys.ESC:
                if (this.seFePositionService.activeName)
                    this.seFeTooltipService.leaveTrigger(this.name);
                break;
            default:
                break;
        }
    }
    onTab(event) {
        if (event.key !== 'Tab')
            return;
        if (this.targetForTooltipTrigger(event.target)) {
            this.seFeTooltipService.enterTrigger(this.name);
        }
    }
    onMouseEnter() {
        this.seFeTooltipService.enterTrigger(this.name);
    }
    nMouseLeave() {
        this.seFeTooltipService.leaveTrigger(this.name);
    }
    get element() {
        return this.elementRef.nativeElement;
    }
    ngOnInit() {
        this.seFePositionService.addTrigger(this);
        this.seFeTooltipService.initTooltip(this.name);
    }
    ngAfterViewInit() {
        this.updateAccessibility();
    }
    ngOnDestroy() {
        this.seFePositionService.removeTrigger(this);
    }
    get trigger() {
        return this.seFePositionService.triggers.find(t => t.instance.name === this.name);
    }
    targetForTooltipTrigger(target) {
        return !!target.closest(`[sefetooltiptrigger^="${this.name}"]`);
    }
    get tooltipInstance() {
        return this.seFePositionService.contents.find(c => c.name === this.name);
    }
    updateAccessibility() {
        const selectors = 'a[href], area, input, select, textarea, button, [tabindex="0"], [contentEditable=true]';
        const elements = this.element.querySelectorAll(selectors);
        if (elements.length === 0) {
            this.element.setAttribute('tabindex', '0');
            this.cdr.detectChanges();
        }
    }
}
SeFeTooltipTriggerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipTriggerDirective, deps: [{ token: i0.ElementRef }, { token: i1.SeFePositionService }, { token: SeFeTooltipService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
SeFeTooltipTriggerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeTooltipTriggerDirective, selector: "[seFeTooltipTrigger]", inputs: { name: ["seFeTooltipTrigger", "name"] }, host: { listeners: { "keydown": "onKedown($event)", "keyup": "onTab($event)", "mouseenter": "onMouseEnter()", "focusout": "nMouseLeave()", "mouseleave": "nMouseLeave()" }, properties: { "style.cursor": "this.cursor", "attr.sefetooltiptrigger": "this.nameProxy", "attr.aria-describedby": "this.desc" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipTriggerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeTooltipTrigger]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1.SeFePositionService }, { type: SeFeTooltipService }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input,
                args: ['seFeTooltipTrigger']
            }], cursor: [{
                type: HostBinding,
                args: ['style.cursor']
            }], nameProxy: [{
                type: HostBinding,
                args: ['attr.sefetooltiptrigger']
            }], desc: [{
                type: HostBinding,
                args: ['attr.aria-describedby']
            }], onKedown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onTab: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], nMouseLeave: [{
                type: HostListener,
                args: ['focusout']
            }, {
                type: HostListener,
                args: ['mouseleave']
            }] } });

class SeFeTooltipModule {
}
SeFeTooltipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeTooltipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipModule, declarations: [SeFeTooltipComponent,
        SeFeTooltipTriggerDirective], imports: [CommonModule], exports: [SeFeTooltipComponent,
        SeFeTooltipTriggerDirective] });
SeFeTooltipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipModule, providers: [SeFePositionModule], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        SeFeTooltipComponent,
                        SeFeTooltipTriggerDirective,
                    ],
                    imports: [CommonModule],
                    exports: [
                        SeFeTooltipComponent,
                        SeFeTooltipTriggerDirective,
                    ],
                    providers: [SeFePositionModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Keys, SeFeTooltipComponent, SeFeTooltipModule, SeFeTooltipTriggerDirective };
